<template>
  <div v-if="visible" class="disclaimer-overlay" @click.self="() => {}">
    <div class="disclaimer-card">
      <h2 class="disclaimer-title">
        <span>风险提示</span>
        <span class="disclaimer-title-en">Warning</span>
      </h2>
      <div class="divider"></div>
      <div v-if="loading" class="disclaimer-loading">
        <span>加载中...</span>
      </div>
      <pre v-else class="disclaimer-text">{{ disclaimerText }}</pre>
      <div class="divider"></div>
      <button
        class="disclaimer-accept-btn"
        :disabled="loading"
        @click="accept"
      >
        <span>我已知晓</span>
        <span class="disclaimer-button-en">Acknowledged</span>
      </button>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, watch } from 'vue';
import { useAuthStore } from '@/stores/auth';
import { authApi } from '@/api/auth';

const authStore = useAuthStore();

const visible = ref(false);
const disclaimerText = ref('');
const loading = ref(true);
const fallbackDisclaimerText =
  '1. 本系统仅对持仓人开放，如全部赎回，将关闭系统入口。\n2. 本系统信息仅供参考，以行政管理人发布的数据为准。';

const showDisclaimer = async () => {
  loading.value = true;
  try {
    const res = await authApi.getDisclaimer();
    const data = res.data?.data || res.data;
    // data.value 为 { zh, en }，默认显示中文
    disclaimerText.value = data?.value?.zh || fallbackDisclaimerText;
    visible.value = true;
  } catch (error) {
    console.warn('获取免责声明失败', error);
    disclaimerText.value = fallbackDisclaimerText;
    visible.value = true;
  } finally {
    loading.value = false;
  }
};

watch(
  () => authStore.isAuthenticated,
  async (isAuthenticated, wasAuthenticated) => {
    if (isAuthenticated && !wasAuthenticated) {
      await showDisclaimer();
    }

    if (!isAuthenticated) {
      visible.value = false;
    }
  }
);

const accept = () => {
  visible.value = false;
};
</script>

<style scoped>
.disclaimer-overlay {
  position: fixed;
  inset: 0;
  z-index: 9999;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(0, 0, 0, 0.5);
  padding: 16px;
}

.disclaimer-card {
  width: 100%;
  max-width: 480px;
  background: #fff;
  border-radius: 12px;
  padding: 32px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.15);
}

.disclaimer-title {
  font-size: 20px;
  font-weight: 700;
  color: #1a1a2e;
  text-align: center;
  margin: 0 0 16px;
}

.disclaimer-title-en {
  margin-left: 10px;
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.04em;
  color: #4b5563;
}

.divider {
  height: 1px;
  background: #e5e7eb;
  margin: 0 0 16px;
}

.disclaimer-loading {
  text-align: center;
  padding: 24px 0;
  color: #6b7280;
  font-size: 14px;
}

.disclaimer-text {
  font-size: 14px;
  color: #4b5563;
  line-height: 1.7;
  white-space: pre-wrap;
  word-break: break-word;
  margin: 0 0 16px;
  font-family: inherit;
}

.disclaimer-accept-btn {
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 10px 0;
  background: #0B71F4;
  color: #fff;
  border: none;
  border-radius: 8px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.2s;
}

.disclaimer-button-en {
  margin-top: 2px;
  font-size: 10px;
  font-weight: 500;
  line-height: 1;
  letter-spacing: 0.06em;
  color: rgba(255, 255, 255, 0.85);
}

.disclaimer-accept-btn:hover {
  background: #095bc4;
}

.disclaimer-accept-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}
</style>
